import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { universalFields, mediaRadiusField } from '../_shared/universal.js';
import { headingHighlightFields } from '../_shared/headingHighlight.js';
/**
 * results — Shrine "Results" at CONFIG-BY-CONFIG parity. A two-column proof band: an editorial side (a
 * heading with the shared word-HIGHLIGHT engine, prose, an image OR a muted-autoplay video, plus a results
 * "content" block — sub-heading, results text, caption, a CTA button and an add-to-cart button) and a
 * results side: a vertical/horizontal list of RESULT ROWS, each rendered as a CSS-animated circular progress
 * RING or a giant percentage NUMBER, with a heading + text.
 *
 * Every Shrine section setting AND the per-row block settings are implemented (same id where sensible,
 * Shrine default + options + group + visible_if → our visibleWhen). The shared heading-highlight (engine 1 +
 * 2) and background-pattern engines are reused verbatim. `layout` swaps which column comes first.
 *
 * DEFERRED — Shopify-PLATFORM only, each rendered nothing off Shopify (reasons mirrored in Component.tsx):
 *  - display_id / visibility ............ Shopify theme-editor section helpers; no off-Shopify meaning.
 *  - color_scheme + custom_colors_* ..... Shopify color-scheme groups; we use the universal `colorScheme`.
 *  - custom_*_image_background .......... Shopify CDN img_url 'master' responsive backgrounds.
 *  - atc_product / atc_skip_cart ........ Shopify product object + cart form; ATC renders as a styled link.
 *  - enable_animations / animation_* .... Shrine load-animation runtime tied to the global
 *                                         settings.enable_load_animations; reveal/scroll entrance animations
 *                                         are explicitly out of scope (the ring still draws via pure CSS).
 */
export const resultsSchema = {
    type: 'results',
    name: 'Results',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── General: heading + highlight ─────────────────────────────────────────
        {
            id: 'title',
            type: 'richtext',
            label: 'Title',
            group: 'General',
            default: { format: 'html', html: '<p>Résultats</p>' },
            info: 'Select words and apply Bold (B) for highlight 1, Italic (I) for highlight 2 — like the Text block.',
        },
        // Heading highlight engine: styles the BOLD (<strong>) words in the title (apply via the Bold button).
        ...headingHighlightFields(1, 'General'),
        {
            id: 'headingSize',
            type: 'select',
            label: 'Heading size',
            group: 'General',
            default: 'h1',
            options: [
                { value: 'h3', label: 'Extra small' },
                { value: 'h2', label: 'Small' },
                { value: 'h1', label: 'Medium' },
                { value: 'h0', label: 'Large' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        {
            id: 'customDesktopSize',
            type: 'range',
            label: 'Custom desktop size',
            group: 'General',
            min: 24,
            max: 60,
            step: 3,
            unit: 'px',
            default: 39,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'customMobileSize',
            type: 'range',
            label: 'Custom mobile size',
            group: 'General',
            min: 20,
            max: 50,
            step: 2,
            unit: 'px',
            default: 28,
            visibleWhen: { field: 'headingSize', equals: 'custom' },
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'General',
            default: {
                format: 'html',
                html: '<p>Parlez des résultats que vos clientes obtiennent avec votre produit.</p>',
            },
        },
        {
            id: 'textSize',
            type: 'range',
            label: 'Text size',
            group: 'General',
            default: 17,
            min: 13,
            max: 28,
            step: 1,
            unit: 'px',
            info: 'Font size of the column paragraph (the heading has its own size above).',
        },
        { id: 'image', type: 'image', label: 'Image', group: 'General' },
        {
            id: 'imagePosition',
            type: 'select',
            label: 'Image position',
            group: 'General',
            default: 'after-text',
            info: 'Where the image sits in the column.',
            options: [
                { value: 'after-title', label: 'Below the title' },
                { value: 'after-text', label: 'Below the text' },
            ],
        },
        {
            id: 'imageWidth',
            type: 'range',
            label: 'Image width',
            group: 'General',
            default: 100,
            min: 30,
            max: 100,
            step: 5,
            unit: '%',
        },
        {
            id: 'imageRatio',
            type: 'select',
            label: 'Image ratio',
            group: 'General',
            default: 'adapt',
            options: [
                { value: 'adapt', label: 'Adapt to image' },
                { value: 'square', label: 'Square (1:1)' },
                { value: 'portrait', label: 'Portrait (4:5)' },
                { value: 'landscape', label: 'Landscape (3:2)' },
            ],
        },
        {
            id: 'imageRadius',
            type: 'range',
            label: 'Image corner radius',
            group: 'General',
            default: 0,
            min: 0,
            max: 40,
            step: 2,
            unit: 'px',
        },
        {
            id: 'video',
            type: 'text',
            label: 'Video URL (mp4)',
            group: 'General',
            placeholder: 'https://…/clip.mp4',
            info: 'A self-hosted mp4. Off Shopify there is no hosted-video object — paste a direct mp4 URL. When set it replaces the image.',
        },
        {
            id: 'videoMutedAutoplay',
            type: 'checkbox',
            label: 'Mute & autoplay video',
            group: 'General',
            default: true,
            visibleWhen: { field: 'video', notEquals: '' },
        },
        // Le disque lecture/pause sur le clip qui démarre seul. Un clip que le visiteur doit lancer garde
        // sa commande quoi qu'il arrive — d'où le `visibleWhen` sur la case ci-dessus.
        playButtonField({
            defaultOn: false,
            group: 'General',
            visibleWhen: [
                { field: 'video', notEquals: '' },
                { field: 'videoMutedAutoplay', equals: true },
            ],
        }),
        // ── General: background pattern (shared engine) ──────────────────────────
        {
            id: 'patternBg',
            type: 'select',
            label: 'Background pattern',
            group: 'General',
            default: 'none',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dotted', label: 'Dots' },
                { value: 'grid', label: 'Grid' },
                { value: 'curved-grid', label: 'Curved grid' },
                { value: 'diagonal-stripes', label: 'Stripes' },
                { value: 'zigzag', label: 'ZigZag' },
                { value: 'arrow-pattern', label: '3D Boxes' },
                { value: 'radial-rings', label: 'Rings' },
                { value: 'diamond-grid-1', label: 'Diamond grid 1' },
                { value: 'diamond-grid-2', label: 'Diamond grid 2' },
                { value: 'quatrefoils', label: 'Quatrefoils' },
                { value: 'ovals', label: 'Ovals' },
                { value: 'hexagons', label: 'Hexagons' },
                { value: 'curvy', label: 'Curvy' },
            ],
        },
        {
            id: 'patternColor',
            type: 'color',
            label: 'Pattern color',
            group: 'General',
            default: { ref: 'theme', token: 'accent' },
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternSize',
            type: 'range',
            label: 'Pattern size / spacing',
            group: 'General',
            min: 4,
            max: 80,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternThickness',
            type: 'range',
            label: 'Line / dot thickness',
            group: 'General',
            min: 1,
            max: 10,
            step: 1,
            unit: 'px',
            default: 2,
            visibleWhen: {
                field: 'patternBg',
                equals: ['dotted', 'grid', 'diagonal-stripes', 'radial-rings'],
            },
        },
        {
            id: 'patternAngle',
            type: 'range',
            label: 'Stripe angle',
            group: 'General',
            min: 0,
            max: 180,
            step: 15,
            unit: 'deg',
            default: 45,
            visibleWhen: { field: 'patternBg', equals: 'diagonal-stripes' },
        },
        {
            id: 'patternOpacityMode',
            type: 'select',
            label: 'Opacity type',
            group: 'General',
            default: 'low',
            options: [
                { value: 'low', label: 'Low' },
                { value: 'normal', label: 'Normal' },
            ],
            visibleWhen: { field: 'patternBg', notEquals: 'none' },
        },
        {
            id: 'patternOpacityLow',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 1,
            max: 20,
            step: 1,
            unit: '%',
            default: 7,
            visibleWhen: { field: 'patternOpacityMode', equals: 'low' },
        },
        {
            id: 'patternOpacityNormal',
            type: 'range',
            label: 'Opacity',
            group: 'General',
            min: 20,
            max: 100,
            step: 5,
            unit: '%',
            default: 80,
            visibleWhen: { field: 'patternOpacityMode', equals: 'normal' },
        },
        // ── Results styling ──────────────────────────────────────────────────────
        {
            id: 'layout',
            type: 'select',
            label: 'Layout',
            group: 'Results styling',
            default: 'results_second',
            options: [
                { value: 'results_first', label: 'Results left, heading right' },
                { value: 'results_second', label: 'Heading left, results right' },
                { value: 'stacked', label: 'Heading on top (stacked)' },
            ],
        },
        {
            id: 'style',
            type: 'select',
            label: 'Result style',
            group: 'Results styling',
            default: 'circle',
            options: [
                { value: 'circle', label: 'Circle (progress ring)' },
                { value: 'number', label: 'Number' },
            ],
        },
        {
            id: 'resultLayout',
            type: 'select',
            label: 'Row layout',
            group: 'Results styling',
            info: 'Rows always stack. This sets each row: circle beside the text (horizontal) or above it, centered (vertical).',
            default: 'horizontal',
            options: [
                { value: 'horizontal', label: 'Circle beside text' },
                { value: 'vertical', label: 'Circle above text' },
            ],
        },
        {
            id: 'resultHorizontalSpacing',
            type: 'range',
            label: 'Horizontal spacing',
            group: 'Results styling',
            min: 10,
            max: 30,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'resultLayout', equals: 'horizontal' },
        },
        {
            id: 'resultVerticalSpacing',
            type: 'range',
            label: 'Vertical spacing',
            group: 'Results styling',
            min: 10,
            max: 50,
            step: 2,
            unit: 'px',
            default: 20,
            visibleWhen: { field: 'resultLayout', equals: 'vertical' },
        },
        {
            id: 'circleNumberSize',
            type: 'range',
            label: 'Circle number size',
            group: 'Results styling',
            min: 10,
            max: 34,
            step: 1,
            unit: 'px',
            default: 18,
            visibleWhen: { field: 'style', equals: 'circle' },
        },
        {
            id: 'numberNumberSize',
            type: 'range',
            label: 'Number size',
            group: 'Results styling',
            min: 30,
            max: 120,
            step: 3,
            unit: 'px',
            default: 45,
            visibleWhen: { field: 'style', equals: 'number' },
        },
        {
            id: 'numberColor',
            type: 'select',
            label: 'Number color',
            group: 'Results styling',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
                { value: 'gradient', label: 'Gradient' },
            ],
        },
        {
            id: 'numberCustomColor',
            type: 'color',
            label: 'Number custom color',
            group: 'Results styling',
            default: { ref: 'custom', value: '#FF0000' },
            visibleWhen: { field: 'numberColor', equals: 'custom' },
        },
        {
            id: 'numberGradient',
            type: 'gradient',
            label: 'Number gradient',
            group: 'Results styling',
            default: 'linear-gradient(136deg, #2F5F8A 0%, #dd1d1d 100%)',
            visibleWhen: { field: 'numberColor', equals: 'gradient' },
        },
        {
            id: 'circleBorderWidth',
            type: 'range',
            label: 'Circle stroke width',
            group: 'Results styling',
            min: 1,
            max: 20,
            step: 1,
            unit: 'px',
            default: 7,
            visibleWhen: { field: 'style', equals: 'circle' },
        },
        {
            id: 'circleDiameter',
            type: 'range',
            label: 'Circle diameter',
            group: 'Results styling',
            min: 30,
            max: 120,
            step: 5,
            unit: 'px',
            default: 70,
            visibleWhen: { field: 'style', equals: 'circle' },
        },
        {
            id: 'circleColor',
            type: 'select',
            label: 'Circle color',
            group: 'Results styling',
            default: 'accent-1',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'text', label: 'Text' },
                { value: 'custom', label: 'Custom' },
                { value: 'gradient', label: 'Gradient' },
            ],
            visibleWhen: { field: 'style', equals: 'circle' },
        },
        {
            id: 'circleCustomColor',
            type: 'color',
            label: 'Circle custom color',
            group: 'Results styling',
            default: { ref: 'custom', value: '#FF0000' },
            visibleWhen: { field: 'circleColor', equals: 'custom' },
        },
        {
            id: 'circleGradient',
            type: 'gradient',
            label: 'Circle gradient',
            group: 'Results styling',
            default: 'linear-gradient(136deg, #2F5F8A 0%, #dd1d1d 100%)',
            visibleWhen: { field: 'circleColor', equals: 'gradient' },
        },
        {
            id: 'circleLinecap',
            type: 'select',
            label: 'Circle line cap',
            group: 'Results styling',
            default: 'unset',
            options: [
                { value: 'unset', label: 'Flat' },
                { value: 'round', label: 'Round' },
            ],
            visibleWhen: { field: 'style', equals: 'circle' },
        },
        {
            id: 'resultHeadingSize',
            type: 'range',
            label: 'Result heading size',
            group: 'Results styling',
            min: 12,
            max: 28,
            step: 1,
            unit: 'px',
            default: 18,
        },
        {
            id: 'resultTextSize',
            type: 'range',
            label: 'Result text size',
            group: 'Results styling',
            min: 10,
            max: 24,
            step: 1,
            unit: 'px',
            default: 15,
        },
        {
            id: 'resultHeadingTextSpacing',
            type: 'range',
            label: 'Heading ↔ text spacing',
            group: 'Results styling',
            min: 0,
            max: 28,
            step: 2,
            unit: 'px',
            default: 6,
        },
        {
            id: 'resultTextLineHeight',
            type: 'range',
            label: 'Result text line height',
            group: 'Results styling',
            min: 1,
            max: 2.5,
            step: 0.1,
            default: 1.6,
        },
        // ── Results content (results-column heading + dividers + caption + alignment) ──
        {
            id: 'resultsHeadline',
            type: 'richtext',
            label: 'Results heading',
            group: 'Results content',
            // No default — an empty results heading renders nothing (the <h3> + its divider only appear once the
            // seller types one). Avoids seeding demo copy the seller then has to hunt down and delete.
            info: 'Optional heading above the result rows (a divider appears under it). Bold (B) = highlight colour.',
        },
        {
            id: 'resultRowDividers',
            type: 'checkbox',
            label: 'Divider between rows',
            group: 'Results content',
            default: true,
            info: 'A thin line between each result row (vertical layout), like Shrine.',
        },
        {
            id: 'resultsHeadlineAlignment',
            type: 'select',
            label: 'Content alignment',
            group: 'Results content',
            default: 'left',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        {
            id: 'caption',
            type: 'richtext',
            label: 'Caption',
            group: 'Results content',
            default: {
                format: 'html',
                html: '<p>Légende à propos des résultats ci-dessous.</p>',
            },
        },
        // ── Section (universal: colorScheme / padding / width) ───────────────────
        mediaRadiusField,
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        ...universalFields,
    ],
    // The RESULT ROW inline block (Shrine `row`): a percentage + heading + text.
    blocks: [
        {
            type: 'row',
            name: 'Result row',
            max: 12,
            fields: [
                {
                    id: 'percentage',
                    type: 'range',
                    label: 'Percentage',
                    min: 0,
                    max: 100,
                    step: 1,
                    unit: '%',
                    default: 90,
                },
                // Richtext (was plain text): the heading takes bold/italic/links like the row text. Older instances
                // still carry a plain STRING — richHeading() renders both, the inspector control tolerates both.
                { id: 'rowHeading', type: 'richtext', label: 'Heading' },
                {
                    id: 'rowText',
                    type: 'richtext',
                    label: 'Text',
                    default: {
                        format: 'html',
                        html: '<p>Constatent que ce produit a nettement amélioré leur quotidien.</p>',
                    },
                },
            ],
        },
    ],
    maxBlocks: 12,
};
export const resultsPresets = [
    {
        name: 'Results',
        settings: {
            // No demo headline in the preset — a new section starts on the neutral field default ("Results"), not
            // fake marketing copy the seller has to remember to replace.
            headingSize: 'h1',
            headingHighlight1: 'solid-color',
            headingHighlight1Color: { ref: 'theme', token: 'accent' },
            text: {
                format: 'html',
                html: '<p>Nos clientes constatent une différence dès les premières semaines. Voici ce qu’elles rapportent.</p>',
            },
            videoMutedAutoplay: true,
            // Results side.
            layout: 'results_second',
            style: 'circle',
            resultLayout: 'horizontal',
            resultHorizontalSpacing: 20,
            circleNumberSize: 18,
            numberColor: 'accent-1',
            circleColor: 'accent-1',
            circleBorderWidth: 7,
            circleDiameter: 70,
            circleLinecap: 'round',
            resultHeadingSize: 18,
            resultTextSize: 15,
            resultHeadingTextSpacing: 6,
            resultTextLineHeight: 1.6,
            // Results content.
            resultsHeadlineSize: 'h2',
            resultsHeadlineAlignment: 'left',
            caption: {
                format: 'html',
                html: '<p>Étude interne menée sur 240 clientes après 8 semaines d’utilisation.</p>',
            },
            // A clean light proof band.
            colorScheme: 'light',
            paddingTop: 72,
            paddingBottom: 72,
            width: 'wide',
        },
        blocks: [
            {
                type: 'row',
                settings: {
                    percentage: 92,
                    rowHeading: { format: 'html', html: '@t:section.result_1' },
                    rowText: {
                        format: 'html',
                        html: '<p>92% des clientes constatent une peau visiblement plus douce.</p>',
                    },
                },
            },
            {
                type: 'row',
                settings: {
                    percentage: 87,
                    rowHeading: { format: 'html', html: '@t:section.result_2' },
                    rowText: {
                        format: 'html',
                        html: '<p>87% rapportent une hydratation qui tient toute la journée.</p>',
                    },
                },
            },
            {
                type: 'row',
                settings: {
                    percentage: 95,
                    rowHeading: { format: 'html', html: '@t:section.result_3' },
                    rowText: {
                        format: 'html',
                        html: '<p>95% recommanderaient le produit à une amie.</p>',
                    },
                },
            },
        ],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const resultsMeta = {
    label: 'Results',
    group: 'Content',
    icon: 'image',
};
//# sourceMappingURL=schema.js.map